"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../../lib/utils'
import { WidgetBase, WidgetBaseProps } from '../core/widget-base'
import { 
  TrendingDown,
  TrendingUp,
  Users,
  ShoppingCart,
  CreditCard,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight,
  ArrowDown,
  Filter,
  Target
} from 'lucide-react'
import { Badge } from '../../ui/badge'
import { Progress } from '../../ui/progress'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip'
import { useSubscription } from '../../../hooks/use-subscription'
import { ProLockScreen } from '../../shared/pro-lock-screen'

export interface FunnelStage {
  id: string
  name: string
  value: number
  percentage?: number
  dropoff?: number
  dropoffPercentage?: number
  icon?: React.ReactNode
  color?: string
  description?: string
  target?: number
  duration?: string
  metadata?: Record<string, any>
}

export interface FunnelData {
  stages: FunnelStage[]
  title?: string
  totalValue?: number
  conversionRate?: number
  currency?: string
  period?: string
  comparison?: {
    value: number
    type: 'increase' | 'decrease'
    period: string
  }
}

export interface FunnelWidgetProps extends Omit<Partial<WidgetBaseProps>, 'variant'> {
  data: FunnelData
  title?: string
  variant?: 'vertical' | 'horizontal' | 'pyramid' | 'steps' | 'cards'
  backgroundVariant?: 'default' | 'minimal' | 'glass' | 'bordered' | 'gradient' | 'colored' | 'overlay' | 'floating'
  gradientDirection?: 'to-t' | 'to-tr' | 'to-r' | 'to-br' | 'to-b' | 'to-bl' | 'to-l' | 'to-tl'
  showValues?: boolean
  showPercentages?: boolean
  showDropoff?: boolean
  showTargets?: boolean
  showDuration?: boolean
  showConversionRate?: boolean
  animate?: boolean

  interactive?: boolean
  onStageClick?: (stage: FunnelStage) => void
}

export function FunnelWidget({
  data,
  title,
  variant = 'vertical',
  backgroundVariant,
  gradientDirection,
  showValues = true,
  showPercentages = true,
  showDropoff = true,
  showTargets = false,
  showDuration = false,
  showConversionRate = true,
  animate = true,

  interactive = true,
  onStageClick,
  ...widgetProps
}: FunnelWidgetProps) {
  const { hasProAccess, isLoading } = useSubscription();
  const [hoveredStage, setHoveredStage] = React.useState<string | null>(null)

  // Format number with max 2 decimal places
  const formatNumber = (value: number): string => {
    if (value % 1 !== 0) {
      return (Math.round(value * 100) / 100).toString()
    }
    return value.toString()
  }

  // Calculate percentages if not provided
  const processedStages = React.useMemo(() => {
    const maxValue = Math.max(...data.stages.map(s => s.value))
    return data.stages.map((stage, index) => {
      const percentage = stage.percentage ?? (stage.value / maxValue) * 100
      const previousValue = index > 0 ? data.stages[index - 1].value : stage.value
      const dropoff = stage.dropoff ?? (previousValue - stage.value)
      const dropoffPercentage = stage.dropoffPercentage ?? 
        (previousValue > 0 ? ((previousValue - stage.value) / previousValue) * 100 : 0)
      
      return {
        ...stage,
        percentage,
        dropoff,
        dropoffPercentage
      }
    })
  }, [data.stages])

  // Default colors for stages
  const getStageColor = (stage: FunnelStage, index: number) => {
    if (stage.color) return stage.color
    
    const defaultColors = [
      '#3b82f6', // blue
      '#8b5cf6', // violet
      '#ec4899', // pink
      '#f59e0b', // amber
      '#10b981', // emerald
      '#06b6d4', // cyan
    ]
    
    return defaultColors[index % defaultColors.length]
  }

  // Get default icon for stage
  const getStageIcon = (stage: FunnelStage, index: number) => {
    if (stage.icon) return stage.icon
    
    const defaultIcons = [
      <Users className="h-4 w-4" />,
      <ShoppingCart className="h-4 w-4" />,
      <CreditCard className="h-4 w-4" />,
      <CheckCircle className="h-4 w-4" />
    ]
    
    return defaultIcons[index] || <Target className="h-4 w-4" />
  }

  // Format value
  const formatValue = (value: number) => {
    if (data.currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: data.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    }
    
    if (value >= 1000000) {
      return `${formatNumber(value / 1000000)}M`
    } else if (value >= 1000) {
      return `${formatNumber(value / 1000)}K`
    }
    return value.toLocaleString()
  }

  // Render vertical funnel
  const renderVerticalFunnel = () => {
    return (
      <div className="space-y-2">
        {processedStages.map((stage, index) => {
          const width = `${stage.percentage}%`
          const color = getStageColor(stage, index)
          const icon = getStageIcon(stage, index)
          const isHovered = hoveredStage === stage.id

          return (
            <motion.div
              key={stage.id}
              className={cn(
                "relative group",
                interactive && "cursor-pointer"
              )}
              onMouseEnter={() => setHoveredStage(stage.id)}
              onMouseLeave={() => setHoveredStage(null)}
              onClick={() => interactive && onStageClick?.(stage)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center gap-4">
                {/* Stage bar */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div 
                        className="p-1.5 rounded"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <div style={{ color }}>{icon}</div>
                      </div>
                      <span className="text-sm font-medium">{stage.name}</span>
                      {stage.description && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <AlertCircle className="h-3 w-3 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{stage.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {showValues && (
                        <span className="text-sm font-bold">{formatValue(stage.value)}</span>
                      )}
                      {showPercentages && (
                        <Badge variant="outline" className="text-xs">
                          {formatNumber(stage.percentage)}%
                        </Badge>
                      )}
                      {showDuration && stage.duration && (
                        <span className="text-xs text-muted-foreground">{stage.duration}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="relative">
                    <div className="h-8 bg-muted/30 rounded overflow-hidden">
                      <motion.div
                        className="h-full rounded transition-all duration-300"
                        style={{ 
                          backgroundColor: color,
                          opacity: isHovered ? 1 : 0.8
                        }}
                        initial={{ width: 0 }}
                        animate={{ width }}
                        transition={{ duration: animate ? 0.5 : 0, delay: index * 0.1 }}
                      />
                    </div>
                    
                    {/* Target indicator */}
                    {showTargets && stage.target && (
                      <div
                        className="absolute top-0 bottom-0 w-0.5 bg-foreground/50"
                        style={{ left: `${(stage.target / Math.max(...data.stages.map(s => s.value))) * 100}%` }}
                      >
                        <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-foreground/50" />
                      </div>
                    )}
                  </div>
                  
                  {/* Connector arrow - right after progress bar */}
                  {index < processedStages.length - 1 && (
                    <div className="flex justify-center mt-2 mb-1">
                      <ArrowDown className="h-4 w-4 text-muted-foreground/50" />
                    </div>
                  )}
                </div>

                {/* Dropoff indicator */}
                {showDropoff && index > 0 && (
                  <div className="flex items-center gap-1 text-xs">
                    <ArrowDown className="h-3 w-3 text-red-500" />
                    <span className="text-red-500 font-medium">
                      -{formatNumber(stage.dropoffPercentage)}%
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          )
        })}
      </div>
    )
  }

  // Render steps funnel
  const renderStepsFunnel = () => {
    return (
      <div className="flex items-center justify-between">
        {processedStages.map((stage, index) => {
          const color = getStageColor(stage, index)
          const icon = getStageIcon(stage, index)
          const isHovered = hoveredStage === stage.id
          const isLast = index === processedStages.length - 1

          return (
            <React.Fragment key={stage.id}>
              <motion.div
                className={cn(
                  "flex flex-col items-center gap-2 flex-1",
                  interactive && "cursor-pointer"
                )}
                onMouseEnter={() => setHoveredStage(stage.id)}
                onMouseLeave={() => setHoveredStage(null)}
                onClick={() => interactive && onStageClick?.(stage)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {/* Step circle */}
                <motion.div
                  className="relative"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div
                    className={cn(
                      "w-16 h-16 rounded-full flex items-center justify-center",
                      "border-2 transition-all duration-300"
                    )}
                    style={{
                      borderColor: color,
                      backgroundColor: isHovered ? `${color}20` : 'transparent'
                    }}
                  >
                    <div style={{ color }}>{icon}</div>
                  </div>
                  
                  {/* Progress ring */}
                  <svg className="absolute inset-0 -rotate-90" width="64" height="64">
                    <circle
                      cx="32"
                      cy="32"
                      r="30"
                      fill="none"
                      stroke={color}
                      strokeWidth="2"
                      strokeDasharray={`${stage.percentage * 1.88} 188`}
                      opacity="0.3"
                    />
                  </svg>
                </motion.div>

                {/* Stage info */}
                <div className="text-center">
                  <div className="font-medium text-sm">{stage.name}</div>
                  {showValues && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatValue(stage.value)}
                    </div>
                  )}
                  {showPercentages && (
                    <Badge variant="outline" className="text-xs mt-1">
                      {formatNumber(stage.percentage)}%
                    </Badge>
                  )}
                </div>
              </motion.div>

              {/* Connector */}
              {!isLast && (
                <div className="flex-shrink-0 px-2">
                  <div className="relative">
                    <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    {showDropoff && (
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs text-red-500 whitespace-nowrap">
                        -{formatNumber(processedStages[index + 1].dropoffPercentage)}%
                      </div>
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  // Render cards funnel
  const renderCardsFunnel = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {processedStages.map((stage, index) => {
          const color = getStageColor(stage, index)
          const icon = getStageIcon(stage, index)
          const isHovered = hoveredStage === stage.id

          return (
            <motion.div
              key={stage.id}
              className={cn(
                "relative p-4 rounded-lg border transition-all",
                interactive && "cursor-pointer hover:shadow-lg",
                isHovered && "border-primary"
              )}
              style={{
                borderColor: isHovered ? color : undefined
              }}
              onMouseEnter={() => setHoveredStage(stage.id)}
              onMouseLeave={() => setHoveredStage(null)}
              onClick={() => interactive && onStageClick?.(stage)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div
                  className="p-2 rounded"
                  style={{ backgroundColor: `${color}20` }}
                >
                  <div style={{ color }}>{icon}</div>
                </div>
                {index > 0 && showDropoff && (
                  <Badge variant="destructive" className="text-xs flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" />
                    {formatNumber(stage.dropoffPercentage)}%
                  </Badge>
                )}
              </div>

              {/* Content */}
              <div className="space-y-2">
                <h4 className="font-semibold">{stage.name}</h4>
                {showValues && (
                  <div className="text-2xl font-bold">{formatValue(stage.value)}</div>
                )}
                {showPercentages && (
                  <Progress value={stage.percentage} className="h-2" />
                )}
                {stage.description && (
                  <p className="text-xs text-muted-foreground">{stage.description}</p>
                )}
                {showTargets && stage.target && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Target</span>
                    <span className="font-medium">{formatValue(stage.target)}</span>
                  </div>
                )}
              </div>
            </motion.div>
          )
        })}
      </div>
    )
  }

  // Render funnel based on variant
  const renderFunnel = () => {
    switch (variant) {
      case 'steps':
        return renderStepsFunnel()
      case 'cards':
        return renderCardsFunnel()
      case 'vertical':
      default:
        return renderVerticalFunnel()
    }
  }

  // Pro access check - moved to the end after all hooks
  if (!isLoading && !hasProAccess) {
    return (
      <ProLockScreen 
        componentName="Funnel Widget" 
        compact={true}
      />
    );
  }

  return (
    <WidgetBase
      title={title}
      variant={backgroundVariant}
      gradientDirection={gradientDirection}
      {...widgetProps}
    >
      <div className="space-y-4">
        {/* Summary stats */}
        {showConversionRate && (
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Overall Conversion Rate</p>
              <p className="text-2xl font-bold">
                {data.conversionRate ? 
                  `${formatNumber(data.conversionRate)}%` : 
                  `${formatNumber((processedStages[processedStages.length - 1].value / processedStages[0].value) * 100)}%`
                }
              </p>
            </div>
            {data.comparison && (
              <div className={cn(
                "flex items-center gap-1",
                data.comparison.type === 'increase' ? 'text-green-600' : 'text-red-600'
              )}>
                {data.comparison.type === 'increase' ? 
                  <TrendingUp className="h-4 w-4" /> : 
                  <TrendingDown className="h-4 w-4" />
                }
                <span className="font-medium">
                  {data.comparison.type === 'increase' ? '+' : '-'}
                  {Math.abs(data.comparison.value)}%
                </span>
                <span className="text-xs text-muted-foreground">
                  vs {data.comparison.period}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Funnel visualization */}
        {renderFunnel()}
      </div>
    </WidgetBase>
  )
}

export default FunnelWidget